import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getSuppliers, getSupplierCategories, syncSuppliersFromXero,
  backfillSuppliersFromBills, createSupplier, bulkDeleteSuppliers,
  findDuplicateSuppliers, mergeSuppliers,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import {
  Building2, Search, ArrowRight, Link2, RefreshCw, Plus, Percent,
  AlertTriangle, ArrowUpDown, Truck, Wrench, Zap, Phone, Car, FileText,
  MoreHorizontal, Trash2, LayoutList, Layers, X, Copy, Crown, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { SupplierSparkline } from '../../components/SupplierSpendChart';

const fmtMoney = (v) => `£${(Number(v) || 0).toLocaleString('en-GB', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

// Small icon badge per category — helps eyeball-scan the list at speed.
const CATEGORY_ICONS = {
  'Builders Merchant': Truck,
  'Electrical Wholesaler': Zap,
  Tools: Wrench,
  Telecoms: Phone,
  Vehicle: Car,
  Office: FileText,
  Subcontractor: Building2,
  Utilities: Zap,
  Software: FileText,
};

const Suppliers = () => {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('All');
  const [sortBy, setSortBy] = useState('spent');   // spent|outstanding|overdue|name
  const [sortDir, setSortDir] = useState('desc');
  const [syncing, setSyncing] = useState(false);
  const [showNew, setShowNew] = useState(false);
  // Bulk-delete state: Set of supplier ids currently ticked.
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  // "Group by category" inserts collapsible section headers between rows.
  // Persisted to localStorage — admin's layout preference sticks.
  const [grouped, setGrouped] = useState(() => {
    try { return localStorage.getItem('suppliers.grouped') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('suppliers.grouped', grouped ? '1' : '0'); } catch {} }, [grouped]);

  // Duplicate-finder dialog state.
  const [showDuplicates, setShowDuplicates] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [list, cats] = await Promise.all([getSuppliers(), getSupplierCategories()]);
      setRows(list);
      setCategories(cats.categories || []);
    } catch (e) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const [bf, xero] = await Promise.allSettled([
        backfillSuppliersFromBills(),
        syncSuppliersFromXero(),
      ]);
      const bfMsg = bf.status === 'fulfilled' ? `+${bf.value.created} from bills` : '';
      const xMsg = xero.status === 'fulfilled'
        ? `+${xero.value.created} new, ${xero.value.linked} linked from Xero`
        : (xero.reason?.response?.data?.detail?.includes('not connected')
            ? '(Xero not connected — skipped)'
            : 'Xero sync failed');
      toast.success(`Sync complete · ${bfMsg} · ${xMsg}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const counts = useMemo(() => {
    const m = { All: rows.length };
    rows.forEach(r => { m[r.category] = (m[r.category] || 0) + 1; });
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    let xs = rows;
    if (activeCat !== 'All') xs = xs.filter(r => r.category === activeCat);
    if (query.trim()) {
      const q = query.toLowerCase();
      xs = xs.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.accountNumber || '').toLowerCase().includes(q) ||
        (r.vatNumber || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'desc' ? -1 : 1;
    const cmp = (a, b) => {
      if (sortBy === 'name') return dir * (a.name || '').localeCompare(b.name || '');
      return dir * ((Number(a[sortBy]) || 0) - (Number(b[sortBy]) || 0));
    };
    return [...xs].sort(cmp);
  }, [rows, activeCat, query, sortBy, sortDir]);

  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    spent: acc.spent + (Number(r.spent) || 0),
    outstanding: acc.outstanding + (Number(r.outstanding) || 0),
    overdue: acc.overdue + (Number(r.overdue) || 0),
  }), { spent: 0, outstanding: 0, overdue: 0 }), [filtered]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(col);
      setSortDir(col === 'name' ? 'asc' : 'desc');
    }
  };

  // ---------- bulk-delete helpers ----------
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleVisible = () => {
    setSelected(prev => {
      const visibleIds = filtered.map(r => r.id);
      const allVisibleSelected = visibleIds.every(id => prev.has(id));
      if (allVisibleSelected) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  };
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const ok = window.confirm(
      `Delete ${ids.length} supplier${ids.length === 1 ? '' : 's'}?\n\n` +
      'Suppliers with linked bills will be skipped to preserve the audit trail.'
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const { deleted = [], skipped = [] } = await bulkDeleteSuppliers(ids);
      clearSelection();
      await reload();
      let msg = '';
      if (deleted.length) msg += `Deleted ${deleted.length}`;
      if (skipped.length) msg += ` · Skipped ${skipped.length} (linked bills)`;
      if (deleted.length) toast.success(msg);
      else if (skipped.length) toast.error('Nothing deleted — all had linked bills');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-suppliers-page">
      <PageHeader
        title="Suppliers"
        description={`${rows.length} supplier${rows.length === 1 ? '' : 's'} on file. Click any row to view details, edit trade discount, or store a member card.`}
        actions={
          <div className="flex items-center gap-1.5">
            <Button
              variant={grouped ? 'default' : 'outline'}
              size="sm"
              onClick={() => setGrouped(g => !g)}
              data-testid="suppliers-group-toggle"
              title={grouped ? 'Showing as flat list' : 'Group by category'}
              className={grouped ? 'bg-slate-900 hover:bg-slate-800' : ''}
            >
              {grouped ? <Layers className="h-4 w-4 sm:mr-1.5" /> : <LayoutList className="h-4 w-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">{grouped ? 'Grouped' : 'Flat'}</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setShowNew(true)}
              data-testid="suppliers-new-btn"
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Plus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">New</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="suppliers-more-btn">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={handleSync} disabled={syncing} data-testid="suppliers-sync-menuitem">
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing…' : 'Sync from Xero & Bills'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowDuplicates(true)} data-testid="suppliers-find-duplicates-menuitem">
                  <Copy className="h-4 w-4 mr-2" />
                  Find duplicates
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    if (filtered.length === 0) return;
                    setSelected(new Set(filtered.map(r => r.id)));
                  }}
                  disabled={filtered.length === 0}
                >
                  Select all visible
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      {/* Bulk-action bar — slides in only when at least one row is ticked. */}
      {selected.size > 0 && (
        <div
          className="sticky top-0 z-20 -mt-2 flex items-center justify-between gap-3 bg-slate-900 text-white rounded-md px-4 py-2.5 shadow-md"
          data-testid="suppliers-bulk-bar"
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={clearSelection}
              className="text-slate-300 hover:text-white"
              aria-label="Clear selection"
              data-testid="suppliers-bulk-clear"
            >
              <X className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium tabular-nums">
              {selected.size} selected
            </span>
          </div>
          <Button
            size="sm"
            variant="destructive"
            onClick={bulkDelete}
            disabled={deleting}
            data-testid="suppliers-bulk-delete-btn"
            className="bg-rose-600 hover:bg-rose-700 text-white border-0"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            {deleting ? 'Deleting…' : `Delete ${selected.size}`}
          </Button>
        </div>
      )}

      {/* AP totals across the current filtered view */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <SummaryCard label="Spent (12 mo.)" value={fmtMoney(totals.spent)} accent="slate" testId="totals-spent" />
        <SummaryCard label="Outstanding" value={fmtMoney(totals.outstanding)} accent="amber" testId="totals-outstanding" />
        <SummaryCard label="Overdue" value={fmtMoney(totals.overdue)} accent="rose" testId="totals-overdue" />
      </div>

      {/* Search + Category tabs */}
      <div className="flex flex-col gap-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="suppliers-search"
            placeholder="Search by name, email, account # or VAT…"
            className="pl-9"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
          {['All', ...categories].map(c => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`category-tab-${c}`}
              className={`shrink-0 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                activeCat === c
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
            >
              {c} <span className={activeCat === c ? 'text-slate-300' : 'text-slate-400'}>· {counts[c] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-slate-500">Loading suppliers…</div>}

      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <Building2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No suppliers yet</div>
          <div className="text-sm text-slate-500 mb-4">
            Click <span className="font-medium">"Sync from Xero & Bills"</span> to import your existing trade accounts,
            or scan a paper receipt — we'll create the supplier automatically.
          </div>
        </div>
      )}

      {/* Desktop: table */}
      {!loading && filtered.length > 0 && (
        <div className="hidden lg:block bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-10 text-center px-3 py-2.5">
                  <Checkbox
                    checked={filtered.length > 0 && filtered.every(r => selected.has(r.id))}
                    onCheckedChange={toggleVisible}
                    aria-label="Select all visible suppliers"
                    data-testid="suppliers-select-all"
                  />
                </th>
                <SortHead label="Supplier" col="name" {...{ sortBy, sortDir, toggleSort }} />
                <th className="text-left px-4 py-2.5">Category</th>
                <SortHead label="Spent (12mo)" col="spent" align="right" {...{ sortBy, sortDir, toggleSort }} />
                <th className="text-center px-2 py-2.5 text-xs uppercase tracking-wide text-slate-500">Trend</th>
                <SortHead label="Outstanding" col="outstanding" align="right" {...{ sortBy, sortDir, toggleSort }} />
                <SortHead label="Overdue" col="overdue" align="right" {...{ sortBy, sortDir, toggleSort }} />
                <th className="text-right px-4 py-2.5">Std discount</th>
                <th className="px-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grouped
                ? renderGroupedRows(filtered, { selected, toggleOne })
                : filtered.map(s => (
                  <SupplierRow key={s.id} s={s} selected={selected.has(s.id)} onToggle={() => toggleOne(s.id)} />
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile: card list */}
      {!loading && filtered.length > 0 && (
        <div className="lg:hidden grid grid-cols-1 gap-3">
          {grouped
            ? renderGroupedCards(filtered, { selected, toggleOne })
            : filtered.map(s => (
              <SupplierCard key={s.id} s={s} selected={selected.has(s.id)} onToggle={() => toggleOne(s.id)} />
            ))
          }
        </div>
      )}

      <NewSupplierDialog
        open={showNew}
        onOpenChange={setShowNew}
        categories={categories}
        onCreated={() => { setShowNew(false); reload(); }}
      />
      <DuplicatesDialog
        open={showDuplicates}
        onOpenChange={setShowDuplicates}
        onMerged={reload}
      />
    </div>
  );
};

// ---------- helpers ----------
const SummaryCard = ({ label, value, accent, testId }) => {
  const cls = {
    slate: 'bg-white border-slate-200 text-slate-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  }[accent];
  return (
    <div className={`border rounded-md p-3 sm:p-4 ${cls}`} data-testid={testId}>
      <div className="text-[10px] sm:text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display text-xl sm:text-2xl tabular-nums mt-1">{value}</div>
    </div>
  );
};

const SortHead = ({ label, col, sortBy, sortDir, toggleSort, align = 'left' }) => (
  <th className={`px-4 py-2.5 ${align === 'right' ? 'text-right' : 'text-left'}`}>
    <button
      onClick={() => toggleSort(col)}
      data-testid={`sort-${col}`}
      className={`inline-flex items-center gap-1 hover:text-slate-900 ${
        sortBy === col ? 'text-slate-900 font-semibold' : ''
      }`}
    >
      {label}
      <ArrowUpDown className={`h-3 w-3 ${sortBy === col ? 'opacity-100' : 'opacity-40'}`} />
      {sortBy === col && (
        <span className="text-[10px]">{sortDir === 'desc' ? '▼' : '▲'}</span>
      )}
    </button>
  </th>
);

const SupplierRow = ({ s, selected, onToggle }) => {
  const Icon = CATEGORY_ICONS[s.category] || Building2;
  return (
    <tr
      className={`hover:bg-slate-50 cursor-pointer transition-colors ${selected ? 'bg-blue-50/60' : ''}`}
      data-testid={`supplier-row-${s.id}`}
      onClick={() => { window.location.href = `/admin/suppliers/${s.id}`; }}
    >
      <td className="w-10 text-center px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          aria-label={`Select ${s.name}`}
          data-testid={`supplier-select-${s.id}`}
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{s.name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-2 truncate">
              {s.accountNumber && <span>A/c {s.accountNumber}</span>}
              {s.xeroContactId && <span className="inline-flex items-center gap-0.5 text-emerald-700"><Link2 className="h-3 w-3" /> Xero</span>}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" className="font-normal">{s.category || 'Other'}</Badge>
      </td>
      <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtMoney(s.spent)}</td>
      <td className="px-2 py-2 align-middle">
        <div className="flex justify-center" onClick={e => e.stopPropagation()}>
          <SupplierSparkline data={s.monthlySpend} />
        </div>
      </td>
      <td className={`px-4 py-3 text-right tabular-nums ${s.outstanding > 0 ? 'text-amber-700 font-medium' : 'text-slate-400'}`}>
        {fmtMoney(s.outstanding)}
      </td>
      <td className={`px-4 py-3 text-right tabular-nums ${s.overdue > 0 ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>
        {s.overdue > 0 && <AlertTriangle className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />}
        {fmtMoney(s.overdue)}
      </td>
      <td className="px-4 py-3 text-right">
        {s.defaultDiscountRate > 0 ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
            <Percent className="h-3 w-3" />{(s.defaultDiscountRate * 100).toFixed(1)}%
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-2 py-3 text-right text-slate-300">
        <ArrowRight className="h-4 w-4 inline" />
      </td>
    </tr>
  );
};

const SupplierCard = ({ s, selected, onToggle }) => {
  const Icon = CATEGORY_ICONS[s.category] || Building2;
  return (
    <div
      className={`bg-white border rounded-md p-4 transition-colors active:bg-slate-50 ${
        selected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-200 hover:border-slate-400'
      }`}
      data-testid={`supplier-card-${s.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
          <Checkbox
            checked={selected}
            onCheckedChange={onToggle}
            aria-label={`Select ${s.name}`}
            data-testid={`supplier-card-select-${s.id}`}
          />
        </div>
        <Link to={`/admin/suppliers/${s.id}`} className="flex items-start gap-3 flex-1 min-w-0">
          <div className="h-9 w-9 rounded bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-slate-900 truncate">{s.name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
              <Badge variant="outline" className="font-normal text-[10px] py-0">{s.category}</Badge>
              {s.xeroContactId && <span className="inline-flex items-center gap-0.5 text-emerald-700"><Link2 className="h-3 w-3" /></span>}
              {s.defaultDiscountRate > 0 && (
                <span className="inline-flex items-center gap-0.5 text-emerald-700">
                  <Percent className="h-3 w-3" />{(s.defaultDiscountRate * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-300 shrink-0 mt-1" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
        <Mini label="Spent" value={fmtMoney(s.spent)} />
        <Mini label="Out." value={fmtMoney(s.outstanding)} accent={s.outstanding > 0 ? 'amber' : ''} />
        <Mini label="Overdue" value={fmtMoney(s.overdue)} accent={s.overdue > 0 ? 'rose' : ''} />
      </div>
    </div>
  );
};

// Group `xs` by category and emit either table rows or mobile cards with
// a section header per category. Categories sort alphabetically with
// "Other" pinned to the bottom — admins typically scan named buckets first.
const groupByCategory = (xs) => {
  const groups = {};
  xs.forEach(r => {
    const cat = r.category || 'Other';
    (groups[cat] = groups[cat] || []).push(r);
  });
  return Object.entries(groups).sort(([a], [b]) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
};

const renderGroupedRows = (xs, { selected, toggleOne }) => {
  const out = [];
  groupByCategory(xs).forEach(([cat, items]) => {
    out.push(
      <tr key={`grp-${cat}`} className="bg-slate-100/70 sticky-cat" data-testid={`supplier-group-${cat}`}>
        <td colSpan={9} className="px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
          {cat} <span className="ml-1.5 text-slate-400 normal-case font-normal">({items.length})</span>
        </td>
      </tr>
    );
    items.forEach(s => {
      out.push(
        <SupplierRow
          key={s.id}
          s={s}
          selected={selected.has(s.id)}
          onToggle={() => toggleOne(s.id)}
        />
      );
    });
  });
  return out;
};

const renderGroupedCards = (xs, { selected, toggleOne }) => {
  const out = [];
  groupByCategory(xs).forEach(([cat, items]) => {
    out.push(
      <div
        key={`grp-${cat}`}
        className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mt-2"
        data-testid={`supplier-group-card-${cat}`}
      >
        {cat} <span className="text-slate-400 normal-case font-normal">({items.length})</span>
      </div>
    );
    items.forEach(s => {
      out.push(
        <SupplierCard
          key={s.id}
          s={s}
          selected={selected.has(s.id)}
          onToggle={() => toggleOne(s.id)}
        />
      );
    });
  });
  return out;
};

const Mini = ({ label, value, accent }) => {
  const cls = accent === 'amber' ? 'text-amber-700' : accent === 'rose' ? 'text-rose-700 font-semibold' : 'text-slate-700';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm tabular-nums ${cls}`}>{value}</div>
    </div>
  );
};

const NewSupplierDialog = ({ open, onOpenChange, categories, onCreated }) => {
  const [form, setForm] = useState({
    name: '', category: 'Other', email: '', phone: '',
    defaultDiscountRate: 0,
  });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({
    name: '', category: 'Other', email: '', phone: '', defaultDiscountRate: 0,
  });

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await createSupplier({
        ...form,
        defaultDiscountRate: Number(form.defaultDiscountRate) || 0,
      });
      toast.success(`Supplier "${form.name}" created`);
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="new-supplier-dialog">
        <DialogHeader>
          <DialogTitle>New supplier</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name *</Label>
            <Input
              data-testid="new-supplier-name"
              autoFocus
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Wickes Building Supplies"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Category</Label>
              <select
                data-testid="new-supplier-category"
                className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm bg-white"
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
              >
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Trade discount %
              </Label>
              <Input
                type="number"
                step="0.5"
                min="0" max="100"
                placeholder="0"
                value={(form.defaultDiscountRate * 100) || ''}
                onChange={e => setForm({ ...form, defaultDiscountRate: (parseFloat(e.target.value) || 0) / 100 })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Phone</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-supplier-save">
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


/**
 * Duplicate supplier finder + merger.
 *
 * Backend returns clusters of suppliers whose normalised names match
 * (lower-case, suffix-stripped — see `_normalise_name`). For each cluster
 * the admin picks ONE keeper (defaults to the first row, which the backend
 * already sorted to put Xero-linked / highest-spend first). All other rows
 * become losers — their bills get re-pointed at the keeper's name and the
 * loser supplier rows are deleted.
 */
const DuplicatesDialog = ({ open, onOpenChange, onMerged }) => {
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState([]);
  // keeperByCluster[clusterKey] → supplier id chosen as the keeper
  const [keepers, setKeepers] = useState({});
  // Which clusters are currently being merged (optimistic spinner)
  const [busy, setBusy] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    findDuplicateSuppliers()
      .then(({ clusters }) => {
        setClusters(clusters);
        // Default keeper = first row (backend already sorts canonical first).
        const defaults = {};
        clusters.forEach(c => { defaults[c.key] = c.suppliers[0]?.id; });
        setKeepers(defaults);
      })
      .catch(() => toast.error('Failed to scan for duplicates'))
      .finally(() => setLoading(false));
  }, [open]);

  const merge = async (cluster) => {
    const keeperId = keepers[cluster.key];
    if (!keeperId) return toast.error('Pick a keeper first');
    const loserIds = cluster.suppliers.filter(s => s.id !== keeperId).map(s => s.id);
    if (loserIds.length === 0) return;
    const keeper = cluster.suppliers.find(s => s.id === keeperId);
    const ok = window.confirm(
      `Merge ${loserIds.length} duplicate${loserIds.length === 1 ? '' : 's'} into "${keeper?.name}"?\n\n` +
      `Bills will be re-pointed at the keeper's name. Other suppliers in the cluster will be deleted.`,
    );
    if (!ok) return;
    setBusy(b => ({ ...b, [cluster.key]: true }));
    try {
      const r = await mergeSuppliers(keeperId, loserIds);
      toast.success(
        `Merged into "${r.keeperName}" — ${r.billsMoved} bill${r.billsMoved === 1 ? '' : 's'} moved` +
        (r.inheritedFields?.length ? `, inherited: ${r.inheritedFields.join(', ')}` : ''),
      );
      // Drop this cluster from the list — next refresh of the parent will
      // reflect the new state of the suppliers table.
      setClusters(cs => cs.filter(c => c.key !== cluster.key));
      onMerged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setBusy(b => ({ ...b, [cluster.key]: false }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-4 w-4 text-slate-700" /> Find duplicate suppliers
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-slate-500">Scanning…</div>}

        {!loading && clusters.length === 0 && (
          <div className="py-12 text-center">
            <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No duplicates found</div>
            <div className="text-sm text-slate-500">Every supplier name is unique after normalisation.</div>
          </div>
        )}

        {!loading && clusters.length > 0 && (
          <div className="space-y-4" data-testid="duplicates-clusters">
            <p className="text-sm text-slate-600">
              Found {clusters.length} likely duplicate group{clusters.length === 1 ? '' : 's'}.
              Tap a row to set it as the <span className="inline-flex items-center gap-0.5 font-medium text-amber-700"><Crown className="h-3 w-3" />keeper</span> — every other row in that group will be merged into it.
            </p>

            {clusters.map(cluster => (
              <div
                key={cluster.key}
                className="border border-slate-200 rounded-md overflow-hidden"
                data-testid={`duplicate-cluster-${cluster.key.replace(/\s+/g, '-')}`}
              >
                <div className="bg-slate-50 px-4 py-2 flex items-center justify-between border-b border-slate-200">
                  <div className="text-xs uppercase tracking-wider text-slate-600">
                    Match: <span className="font-mono normal-case bg-slate-200 px-1.5 py-0.5 rounded text-slate-800">{cluster.key}</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => merge(cluster)}
                    disabled={busy[cluster.key]}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid={`duplicate-merge-btn-${cluster.key.replace(/\s+/g, '-')}`}
                  >
                    {busy[cluster.key] ? 'Merging…' : <><ArrowDown className="h-3.5 w-3.5 mr-1.5" /> Merge</>}
                  </Button>
                </div>
                <ul className="divide-y divide-slate-100">
                  {cluster.suppliers.map(s => {
                    const isKeeper = keepers[cluster.key] === s.id;
                    return (
                      <li
                        key={s.id}
                        onClick={() => setKeepers(k => ({ ...k, [cluster.key]: s.id }))}
                        className={`px-4 py-3 cursor-pointer transition-colors flex items-center gap-3 ${
                          isKeeper
                            ? 'bg-amber-50 border-l-2 border-amber-500'
                            : 'bg-white hover:bg-slate-50 border-l-2 border-transparent opacity-90'
                        }`}
                        data-testid={`duplicate-row-${s.id}`}
                      >
                        <div className="shrink-0">
                          {isKeeper ? (
                            <Crown className="h-4 w-4 text-amber-600" />
                          ) : (
                            <span className="block h-4 w-4 rounded-full border border-slate-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                            {s.name}
                            {s.xeroContactId && (
                              <span className="inline-flex items-center gap-0.5 text-emerald-700 text-xs">
                                <Link2 className="h-3 w-3" /> Xero
                              </span>
                            )}
                            {isKeeper && (
                              <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">
                                Keeper
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                            <span>{s.billCount || 0} bill{s.billCount === 1 ? '' : 's'}</span>
                            {Number(s.spent) > 0 && <span>· {fmtMoney(s.spent)} spent</span>}
                            {s.email && <span>· {s.email}</span>}
                            {s.accountNumber && <span>· A/c {s.accountNumber}</span>}
                            {s.vatNumber && <span>· VAT {s.vatNumber}</span>}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Suppliers;
